import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { api, formatPKR } from '../api';
import PageHeader from '../components/PageHeader';
import ClickableStatCard from '../components/ClickableStatCard';
import StatusBadge from '../components/StatusBadge';
import { ownerFromSlug } from '../utils/constants';
import ReportExportBar from '../components/ReportExportBar';
import { runOwnerReportExport } from '../utils/reportExports';

const emptyForm = {
  amount: '',
  notes: '',
  paymentMethod: 'Bank Transfer',
  status: 'Pending',
};

export default function OwnerDashboard() {
  const { slug } = useParams();
  const ownerName = ownerFromSlug(slug);
  const [finance, setFinance] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [investmentAmount, setInvestmentAmount] = useState('');
  const [savingInvestment, setSavingInvestment] = useState(false);
  const [editId, setEditId] = useState(null);

  const load = () =>
    api
      .getOwnerFinance(ownerName)
      .then(setFinance)
      .catch((e) => setError(e.message));

  useEffect(() => {
    load();
  }, [ownerName]);

  useEffect(() => {
    if (finance?.owner?.investmentAmount != null) {
      setInvestmentAmount(String(finance.owner.investmentAmount));
    }
  }, [finance]);

  const handleInvestmentSave = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    const amount = Number(investmentAmount);
    setSavingInvestment(true);
    try {
      await api.updateOwnerInvestment(ownerName, amount);
      setFinance((prev) => ({
        ...prev,
        owner: { ...prev.owner, investmentAmount: amount },
      }));
      setMessage('Investment amount updated');
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingInvestment(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    try {
      if (editId) {
        await api.updateWithdrawal(editId, {
          amount: Number(form.amount),
          notes: form.notes,
          paymentMethod: form.paymentMethod,
          status: form.status,
        });
        setMessage('Withdrawal updated');
        setEditId(null);
      } else {
        await api.recordWithdrawal({
          ownerName,
          amount: Number(form.amount),
          notes: form.notes,
          paymentMethod: form.paymentMethod,
          status: form.status,
          approvedBy: 'Admin',
        });
        setMessage('Withdrawal added');
      }
      setForm(emptyForm);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const startEdit = (w) => {
    setEditId(w.id);
    setForm({
      amount: String(w.amount),
      notes: w.notes,
      paymentMethod: w.paymentMethod,
      status: w.status,
    });
  };

  if (error && !finance) return <p className="text-red-600">{error}</p>;
  if (!finance) return <p className="text-lumora-text/50">Loading owner dashboard...</p>;

  return (
    <div>
      <div className="mb-4">
        <Link to="/" className="text-sm text-lumora-wood hover:underline">
          ← Back to Dashboard
        </Link>
      </div>
      <PageHeader
        title={`${finance.owner.name} — Financial Dashboard`}
        subtitle={`${finance.owner.role} · ${finance.owner.sharePercent}% profit share`}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-8">
        <ClickableStatCard
          label="Investment Amount"
          value={formatPKR(finance.owner.investmentAmount ?? 0)}
          accent
        />
        <ClickableStatCard label="Total Earnings" value={formatPKR(finance.totalEarnings)} accent />
        <ClickableStatCard label="Total Withdrawn" value={formatPKR(finance.totalWithdrawn)} />
        <ClickableStatCard label="Available Balance" value={formatPKR(finance.availableBalance)} accent />
        <ClickableStatCard label="Last Withdrawal" value={formatPKR(finance.lastWithdrawalAmount)} />
        <ClickableStatCard
          label="Last Withdrawal Date"
          value={finance.lastWithdrawalDate || '—'}
        />
      </div>

      <div className="glass-card mb-8 max-w-lg">
        <h3 className="font-serif font-medium text-lumora-dark-wood mb-4">Update Investment</h3>
        <form onSubmit={handleInvestmentSave} className="flex flex-wrap gap-3">
          <input
            type="number"
            min="0"
            step="1"
            placeholder="Investment amount (PKR)"
            value={investmentAmount}
            onChange={(e) => setInvestmentAmount(e.target.value)}
            className="input flex-1 min-w-[200px]"
            required
          />
          <button type="submit" className="btn-primary whitespace-nowrap" disabled={savingInvestment}>
            {savingInvestment ? 'Saving…' : 'Save Investment'}
          </button>
        </form>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4 mb-8">
        <ClickableStatCard label="Lifetime Earnings" value={formatPKR(finance.earnings.lifetime)} />
        <ClickableStatCard label="Yearly Earnings" value={formatPKR(finance.earnings.yearly)} />
        <ClickableStatCard label="Monthly Earnings" value={formatPKR(finance.earnings.monthly)} accent />
        <ClickableStatCard label="Weekly Earnings" value={formatPKR(finance.earnings.weekly)} />
        <ClickableStatCard label="Today's Earnings" value={formatPKR(finance.earnings.today)} accent />
      </div>

      <div className="grid lg:grid-cols-2 gap-8 mb-8">
        <div className="glass-card">
          <h3 className="font-serif font-medium text-lumora-dark-wood mb-4">Monthly Earnings</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={finance.monthlyEarnings}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEE6D8" />
              <XAxis dataKey="month" stroke="#8B5E3C" tick={{ fontSize: 10 }} />
              <YAxis stroke="#8B5E3C" tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => formatPKR(v)} />
              <Bar dataKey="earnings" fill="#8B5E3C" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="glass-card">
          <h3 className="font-serif font-medium text-lumora-dark-wood mb-4">Monthly Withdrawals</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={finance.monthlyWithdrawals}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEE6D8" />
              <XAxis dataKey="month" stroke="#8B5E3C" tick={{ fontSize: 10 }} />
              <YAxis stroke="#8B5E3C" tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => formatPKR(v)} />
              <Bar dataKey="amount" fill="#D4B483" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="glass-card lg:col-span-2">
          <h3 className="font-serif font-medium text-lumora-dark-wood mb-4">Net Balance Trend</h3>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={finance.netBalanceTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEE6D8" />
              <XAxis dataKey="month" stroke="#8B5E3C" tick={{ fontSize: 10 }} />
              <YAxis stroke="#8B5E3C" tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => formatPKR(v)} />
              <Legend />
              <Line type="monotone" dataKey="earnings" stroke="#8B5E3C" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="withdrawals" stroke="#D4B483" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="net" stroke="#5D3A23" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="glass-card mb-8">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="font-serif font-medium text-lumora-dark-wood">
            {editId ? 'Edit Withdrawal' : 'Add Withdrawal'}
          </h3>
        </div>
        <form onSubmit={handleSubmit} className="grid md:grid-cols-2 xl:grid-cols-4 gap-3">
          <input
            type="number"
            min="1"
            placeholder="Amount"
            value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
            className="input"
            required
          />
          <select
            value={form.paymentMethod}
            onChange={(e) => setForm({ ...form, paymentMethod: e.target.value })}
            className="input"
          >
            <option>Bank Transfer</option>
            <option>Cash</option>
            <option>Cheque</option>
          </select>
          <select
            value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value })}
            className="input"
          >
            <option value="Pending">Pending</option>
            <option value="Completed">Completed</option>
          </select>
          <input
            placeholder="Notes"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            className="input"
          />
          <div className="md:col-span-2 xl:col-span-4 flex gap-2">
            <button type="submit" className="btn-primary">
              {editId ? 'Update Withdrawal' : 'Add Withdrawal'}
            </button>
            {editId && (
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setEditId(null);
                  setForm(emptyForm);
                }}
              >
                Cancel
              </button>
            )}
          </div>
        </form>
        {error && <p className="text-red-600 text-sm mt-3">{error}</p>}
        {message && <p className="text-green-700 text-sm mt-3">{message}</p>}
      </div>

      <div className="glass-card">
        <h3 className="font-serif font-medium text-lumora-dark-wood mb-4">Withdrawal History</h3>
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Date</th>
                <th>Amount</th>
                <th>Payment Method</th>
                <th>Notes</th>
                <th>Approved By</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {finance.withdrawals.map((w) => (
                <tr key={w.id}>
                  <td>{w.id}</td>
                  <td>{w.date}</td>
                  <td>{formatPKR(w.amount)}</td>
                  <td>{w.paymentMethod}</td>
                  <td>{w.notes || '—'}</td>
                  <td>{w.approvedBy}</td>
                  <td>
                    <StatusBadge status={w.status} />
                  </td>
                  <td className="space-x-1 whitespace-nowrap">
                    <button type="button" className="btn-secondary text-xs" onClick={() => startEdit(w)}>
                      Edit
                    </button>
                    {w.status === 'Pending' && (
                      <>
                        <button
                          type="button"
                          className="btn-primary text-xs"
                          onClick={() => api.approveWithdrawal(w.id).then(load)}
                        >
                          Approve
                        </button>
                        <button
                          type="button"
                          className="btn-danger text-xs"
                          onClick={() => api.rejectWithdrawal(w.id).then(load)}
                        >
                          Reject
                        </button>
                      </>
                    )}
                    <button
                      type="button"
                      className="btn-danger text-xs"
                      onClick={() => {
                        if (window.confirm('Delete this withdrawal?')) {
                          api.deleteWithdrawal(w.id).then(load);
                        }
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {finance.withdrawals.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center text-lumora-text/50 py-8">
                    No withdrawals yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ReportExportBar
        onExport={(params) => runOwnerReportExport({ ...params, finance })}
      />
    </div>
  );
}
